import { css } from 'aphrodite';
import { parseAsString, useQueryState } from 'nuqs';
import { useLayoutEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Redirect } from 'react-router-dom';
import { useCoachingConversationCallView } from '../../../queries/AIConversation/useCoachingConversationCallView';
import { Button } from '../../../web/components-v2/Button';
import {
	AppDrawerContent,
	AppDrawerToolbarButton,
	CoffeeDrawerProvider,
	CoffeeDrawerToolbar,
	SideDrawer,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import { BasicPage } from '../BasicPage';
import { AdherencePanel } from './AdherencePanel';
import { AdherenceScoreboard } from './AdherenceScoreboard';
import { CallLogLink } from './callLinks';
import { CallScoringDrawerContent } from './CallScoringDrawerContent';
import { styleSheet } from './styles';
import { TranscriptPanel } from './TranscriptPanel';

const DRAWER_VALUE_SCORING = 'ai-coaching-review-scoring';

const PAGE_TITLE = 'AI Coaching Review';

const ScoringToolbarIcon = () => (
	<svg
		width='16'
		height='16'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<path d='M9 4h6a1 1 0 0 1 1 1v1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h1V5a1 1 0 0 1 1-1z' />
		<path d='M9 13l2 2 4-4' />
	</svg>
);

export function CoffeeAICoachingReview() {
	// The aida app mounts the react-router-v5 nuqs adapter, so this reads/writes the hash query string.
	const [selectedConversationId, setSelectedConversationId] = useQueryState('convo', parseAsString);
	const [drawerValue, setDrawerValue] = useState('');
	const isMobile = useMediaQuery({ maxWidth: THEME.breakpoints.md });
	const { hasManagerPermissions, isLoading: permissionsLoading } = useManagerPermissions();

	const { callView, notFound } = useCoachingConversationCallView(selectedConversationId);

	// Board and call share one scroller, so switching between them would otherwise keep the other view's offset.
	const pageColumnRef = useRef<HTMLDivElement>(null);
	const isCallOpen = Boolean(selectedConversationId);
	useLayoutEffect(() => {
		if (pageColumnRef.current) {
			pageColumnRef.current.scrollTop = 0;
		}
	}, [isCallOpen]);

	if (permissionsLoading) {
		return (
			<BasicPage appearance='light' pageStyles={[styleSheet.fullWidthPage]} title={PAGE_TITLE}>
				<div className={css(styleSheet.spinnerState)}>
					<LoadingSpinner type='large' />
				</div>
			</BasicPage>
		);
	}

	// The route's `onLoad` can't gate this — manager role resolves asynchronously.
	if (!hasManagerPermissions) {
		return <Redirect to='/' />;
	}

	// Dismissing the detail view undoes the entry that opened it rather than stacking another.
	const backToBoard = () => setSelectedConversationId(null, { history: 'replace' });

	return (
		<CoffeeDrawerProvider value={drawerValue} onValueChange={setDrawerValue}>
			<div className={css(styleSheet.root)}>
				<div ref={pageColumnRef} className={css(styleSheet.pageColumn)}>
					<BasicPage
						appearance='light'
						pageStyles={[styleSheet.fullWidthPage]}
						title={PAGE_TITLE}
						headerActions={
							isCallOpen ? (
								<>
									<CallLogLink phoneCallId={callView?.phoneCall?.id} />
									<Button variant='secondary' size='sm' onClick={backToBoard}>
										← Back to adherence
									</Button>
								</>
							) : null
						}
					>
						<div className={css(isCallOpen ? styleSheet.boardPaneHidden : null)}>
							<AdherenceScoreboard />
						</div>

						{isCallOpen ? (
							<section className={css(styleSheet.panel)}>
								{notFound ? (
									<div className={css(styleSheet.detailNotFound)}>
										This conversation could not be found. It may have been deleted, or the link is invalid.
									</div>
								) : (
									<>
										<AdherencePanel conversationId={selectedConversationId} />
										<TranscriptPanel conversationId={selectedConversationId} />
									</>
								)}
							</section>
						) : null}
					</BasicPage>
				</div>

				{!isMobile && isCallOpen ? (
					<SideDrawer>
						<AppDrawerContent value={DRAWER_VALUE_SCORING} unmountOnExit>
							<CallScoringDrawerContent conversationId={selectedConversationId} onClose={() => setDrawerValue('')} />
						</AppDrawerContent>
						<CoffeeDrawerToolbar>
							<AppDrawerToolbarButton value={DRAWER_VALUE_SCORING} tooltip='Call scoring & details'>
								<ScoringToolbarIcon />
							</AppDrawerToolbarButton>
						</CoffeeDrawerToolbar>
					</SideDrawer>
				) : null}
			</div>
		</CoffeeDrawerProvider>
	);
}
